import React, { useEffect } from 'react';
import { Button, ModalIsland } from '@saviynt/design-system';
import cx from 'classnames';
import { useHistory } from 'react-router-dom';

import { SaviChatHeader, SaviContentPanel } from '../../components';
import { useChatOverlayContext } from '../../context/chatOverlayContext';
import { useIspmContext } from '../../context/ispmContext';
import { useIspmErrorContext } from '../../context/ispmErrorContext';
import {
  useMultipleTabCheck,
  useResponsiveDataPanelFullScreen,
} from '../../hooks';
import useMessageHandlers from '../../hooks/use-messagehandlers';
import useSaviChatAskApi from '../../hooks/use-saviapi';
import { Logger } from '../../services';
import config from '../../services/ispmConfig';
import { useInvertedLocalize } from '../../utils/localization';
import { invertedMessages } from '../../utils/messages';

import { SaviChatFooter, SaviChatHistory } from './index';

import './SaviChat.css';

/**
 * SaviChat Component
 *
 * This component handles the SaviChat overlay, managing chat history, footer, and generated content display.
 * It interacts with ISPM state, Chat Overlay state, and the chat API.
 *
 * @component
 * @returns {JSX.Element} The SaviChat component that renders chat functionality and generated content.
 */
export default function SaviChat() {
  // Extract ISPM and Chat Overlay states and their corresponding update functions
  const { ispmState, updateIspmState } = useIspmContext();
  const { chatOverlayState } = useChatOverlayContext();
  const { isActiveTab: isActiveChatTab, showModal: isErrorModalVisible } =
    useMultipleTabCheck();

  const history = useHistory();
  const l = useInvertedLocalize(invertedMessages);

  // Extract the function to handle chat ask API in the chat overlay
  const { handleChatAskApiInChatOverlay } = useSaviChatAskApi();

  const {
    updateAutomatedUserMessageInSession,
    updateAutomatedApiResponseInSession,
  } = useMessageHandlers();

  // TabletSmall breakpoint to make data panel by default full screen once open
  useResponsiveDataPanelFullScreen(834);

  const { updateIspmErrorState } = useIspmErrorContext();
  /**
   * useEffect hook to trigger the chat API call when the chatuuid or user query in ISPM state changes.
   *
   * @effect
   * @param {string} ispmState.chatuuid - The unique identifier for the chat session.
   * @param {string} ispmState.userQuery - The query submitted by the user.
   */

  useEffect(() => {
    if (!isActiveChatTab) {
      Logger.info('Current tab is not active tab. blocking api request.');

      return;
    }

    if (chatOverlayState.isApiCallInProgress) {
      Logger.info('Chat ask API in progress. blocking the api call request');

      return;
    }

    if (ispmState.userQuery.length === 0) {
      Logger.info('User query is empty string. Blocking the api call');

      return;
    }

    handleChatAskApiInChatOverlay({
      chatuuid: ispmState.chatuuid,
      freeText: ispmState.userQuery,
    });
  }, [
    chatOverlayState.isApiCallInProgress,
    handleChatAskApiInChatOverlay,
    isActiveChatTab,
    ispmState.chatuuid,
    ispmState.userQuery,
  ]);

  /**
   * Add and entry in chat history - to tell user - context has been refresh
   *
   * @function onChatReset
   */
  const handleChatReset = () => {
    // refresh the context
    updateIspmState('chatuuid', null);
    updateIspmErrorState('errorType', undefined);
    // TODO: get confirmation from @Ashish/@Sagar
    // JIRA: https://saviyntars.atlassian.net/browse/IN-12517?focusedCommentId=1349027
    // updateChatOverlayState('inputDisabled', false);
    // updateChatOverlayState('isApiCallInProgress', false);
    updateAutomatedUserMessageInSession(config.RESET_ENTRY.userQuery, true);
    updateAutomatedApiResponseInSession(config.RESET_ENTRY.response);
  };

  const handleChatClose = () => {
    updateIspmErrorState('errorType', undefined);
    updateIspmState('isChatOverlayVisible', false);
  };

  // render the blocking modal if this is not the active chat tab
  if (isErrorModalVisible) {
    return (
      <ModalIsland
        kind='warning'
        alertTitle={l('Another Session in Progress')}
        alertSubtitle={l(
          'Another Copilot session is open in another tab. Close this session to navigate out of Copilot and continue, or close any duplicate tabs.'
        )}
        onClose={() => {}}
        isOpen
        primaryButton={
          <Button
            type='button'
            kind='outlined'
            size='medium'
            onClick={() => history.push('/')}>
            {l('Close Session')}
          </Button>
        }
        secondaryButton={null}
        // lock down the modal
        isCloseButtonVisible={false}
        isCloseOnEscapeKeyEnabled={false}
      />
    );
  }

  return (
    <section className='Ispm-SaviChatPage'>
      <SaviChatHeader
        onChatClose={handleChatClose}
        onChatReset={handleChatReset}
      />
      <div className='Ispm-SaviChatPage-content'>
        {chatOverlayState.showGeneratedContent && (
          <section
            className={cx({
              'Ispm-SaviChatPage-content-dataPanel': true,
              'Ispm-SaviChatPage-content-dataPanel--isExpanded':
                chatOverlayState.isDataPanelFullScreen,
            })}
            aria-label={l('Data panel with sql and visualization content')}>
            <SaviContentPanel
              kind={chatOverlayState.contentPanelKind}
              data={chatOverlayState.contentPanelData}
            />
          </section>
        )}
        <section
          className={cx({
            'Ispm-SaviChatPage-content-chatpanel': true,
            'Ispm-SaviChatPage-content-chatpanel--isSplitView':
              chatOverlayState.showGeneratedContent,
            'Ispm-SaviChatPage-content-chatpanel--isSplitView--isMenuPinned':
              chatOverlayState.showGeneratedContent && ispmState.isMenuPinned,
          })}>
          <SaviChatHistory />
          <SaviChatFooter />
        </section>
      </div>
    </section>
  );
}
